import { useCallback, useRef } from "react";
import type { RefObject } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

export interface UseVirtualRowsOptions {
  count: number;
  /** Row height in px: one number for fixed rows, or a function of the row index. */
  estimateSize: number | ((index: number) => number);
  /** Rows rendered beyond the visible window on each side. Default 8. */
  overscan?: number;
  /** Stable key per row; defaults to the index. */
  getItemKey?: (index: number) => string | number;
  /** Use this scroll element instead of the one the hook returns in `scrollRef`. */
  externalScrollRef?: RefObject<HTMLElement | null>;
}

export interface VirtualRow {
  index: number;
  key: string | number;
  /** Offset from the top of the list, in px. */
  start: number;
  size: number;
}

export interface UseVirtualRowsResult {
  /** Attach to the scroll container. */
  scrollRef: RefObject<HTMLDivElement | null>;
  items: VirtualRow[];
  /** Full height of all rows, in px. */
  totalSize: number;
  scrollToIndex: (index: number, align?: "start" | "center" | "end" | "auto") => void;
}

const DEFAULT_OVERSCAN = 8;

/** The only wrapper over @tanstack/react-virtual: windowed rows for one scroll container. */
export const useVirtualRows = ({
  count,
  estimateSize,
  overscan = DEFAULT_OVERSCAN,
  getItemKey,
  externalScrollRef,
}: UseVirtualRowsOptions): UseVirtualRowsResult => {
  const ownRef = useRef<HTMLDivElement | null>(null);
  const sizeOf = useCallback(
    (index: number) => (typeof estimateSize === "function" ? estimateSize(index) : estimateSize),
    [estimateSize],
  );
  const virtualizer = useVirtualizer({
    count,
    overscan,
    estimateSize: sizeOf,
    getItemKey,
    getScrollElement: () => (externalScrollRef ?? ownRef).current,
  });
  const items = virtualizer.getVirtualItems().map((row) => ({
    index: row.index,
    key: row.key as string | number,
    start: row.start,
    size: row.size,
  }));
  const scrollToIndex = useCallback(
    (index: number, align: "start" | "center" | "end" | "auto" = "auto") => virtualizer.scrollToIndex(index, { align }),
    [virtualizer],
  );
  return { scrollRef: ownRef, items, totalSize: virtualizer.getTotalSize(), scrollToIndex };
};

export default useVirtualRows;
